import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import {
  REASSIST_ARCHIVE_PATH,
  REASSIST_LABELS,
  REASSIST_REQUEST_PATH,
  REASSIST_STATUS_PATH,
} from "../config";
import { useReassistArchiveEnabled } from "../hooks/useReassistArchiveEnabled";
import { focusRingSx, rowHoverBg } from "../utils/tokens";
import { REASSIST_LOGO_HEIGHT, ReassistLogo } from "./ReassistLogo";

interface NavRowProps {
  to: string;
  label: string;
  icon: SvgIconComponent;
  /** 開いている画面の行なら true（選択中の見た目と aria-current="page"）。 */
  active: boolean;
  onNavigate?: () => void;
}

/** 行き先（画面）を表すメニュー行。他のサイドバーの行と同じ高さ・左端・アイコン列にそろえる。 */
const NavRow: React.FC<NavRowProps> = ({
  to,
  label,
  icon: Icon,
  active,
  onNavigate,
}) => {
  const theme = useTheme();
  return (
    <ListItemButton
      component={RouterLink}
      to={to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      sx={{
        minHeight: 44,
        px: 2,
        py: 0,
        backgroundColor: active ? rowHoverBg(theme) : "transparent",
        "&:hover": { backgroundColor: rowHoverBg(theme) },
        ...focusRingSx(theme),
      }}
    >
      <ListItemIcon sx={{ minWidth: 28 }}>
        <Icon
          aria-hidden
          sx={{
            fontSize: 16,
            color: active ? "primary.main" : "darkgray.main",
          }}
        />
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: { xs: 16, [MOBILE_BREAKPOINT]: 14 },
          fontWeight: 500,
          color: active ? "primary.main" : "black.main",
        }}
        sx={{ m: 0 }}
      />
    </ListItemButton>
  );
};

interface Props {
  /** リンクを押したときの処理（スマホのメニューを閉じるのに使う）。 */
  onNavigate?: () => void;
}

/**
 * サイドバーに置く R.E.ASSIST（役所資料の取得代行）の入口（展開時）。
 * 箱に入れず、上下の線で他のメニューと区切る。ロゴ（説明はツールチップ）、「役所資料を依頼する」「役所資料を探す」「依頼状況・請求」のメニュー行の順に置く。
 * 依頼状況・過去の依頼・請求は /reassist、リユース資料の購入は /reassist/archive に表示する。
 */
export const ReassistSideMenuSection: React.FC<Props> = ({ onNavigate }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const isReadOnly = useReadOnlyUser();
  const archiveEnabled = useReassistArchiveEnabled();
  return (
    <Box
      component="section"
      aria-label={REASSIST_LABELS.service}
      sx={{
        my: 1,
        pt: 2,
        pb: 1,
        borderTop: `1px solid ${theme.palette.border.main}`,
        borderBottom: `1px solid ${theme.palette.border.main}`,
        backgroundColor: theme.palette.white.main,
      }}
    >
      <Box sx={{ px: 2 }}>
        {/* 説明はツールチップで出す（サイドバーの常時表示を減らす）。キーボードでも出るよう focus できるようにする */}
        <Tooltip
          title={REASSIST_LABELS.sideMenuTooltip}
          placement="right"
          arrow
        >
          <Box
            tabIndex={0}
            sx={{
              display: "inline-block",
              borderRadius: 0.5,
              ...focusRingSx(theme),
            }}
          >
            <ReassistLogo height={REASSIST_LOGO_HEIGHT.sideMenu} />
          </Box>
        </Tooltip>
      </Box>
      {/* ボタンにせず、他のメニューと同じ行にそろえる（ボタンだけが浮いて見えるため）。ロゴとの間は 16px 空ける */}
      <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth>
        <ListItemButton
          component={RouterLink}
          to={REASSIST_REQUEST_PATH}
          disabled={isReadOnly}
          onClick={onNavigate}
          sx={{
            mt: 2,
            minHeight: 44,
            px: 2,
            py: 0,
            "&:hover": { backgroundColor: rowHoverBg(theme) },
            ...focusRingSx(theme),
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <AddIcon aria-hidden sx={{ fontSize: 16, color: "primary.main" }} />
          </ListItemIcon>
          <ListItemText
            primary={REASSIST_LABELS.request}
            primaryTypographyProps={{
              fontSize: { xs: 16, [MOBILE_BREAKPOINT]: 14 },
              fontWeight: 700,
              color: "primary.main",
            }}
            sx={{ m: 0 }}
          />
        </ListItemButton>
      </ReadOnlyTooltip>
      {archiveEnabled ? (
        <NavRow
          to={REASSIST_ARCHIVE_PATH}
          label={REASSIST_LABELS.archive}
          icon={SearchOutlined}
          active={pathname === REASSIST_ARCHIVE_PATH}
          onNavigate={onNavigate}
        />
      ) : null}
      <NavRow
        to={REASSIST_STATUS_PATH}
        label={REASSIST_LABELS.status}
        icon={ReceiptLongOutlined}
        active={pathname === REASSIST_STATUS_PATH}
        onNavigate={onNavigate}
      />
    </Box>
  );
};
